import React, { useContext } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import { colors } from '@atlaskit/theme';

import {
  APPROVAL_ERROR,
  APPROVAL_SUCCESS,
} from 'src/redux/pull-request/actions';
import { ITERATIVE_REVIEW_COMPLETED } from 'src/redux/pull-request/iterative-review-reducer';
import {
  getCurrentPullRequestUrlPieces,
  getPullRequestSourceHash,
} from 'src/redux/pull-request/selectors';
import {
  useApprovePullRequestWithMetrics,
  useUnapprovePullRequest,
} from 'src/sections/repository/sections/pull-request/components/header-actions/header-action-hooks';
import { updateLastReviewedCommit } from 'src/sections/repository/sections/pull-request/components/iterative-review/utils';
import ReviewerActionButton from 'src/sections/repository/sections/pull-request/components/pull-request-reviewer-action-button';
import { KeyboardShortcutTooltip } from 'src/sections/repository/sections/pull-request/components/tabs/shortcuts/keyboard-shortcut-tooltip';
import { KeyboardShortcutsContext } from 'src/sections/repository/sections/pull-request/components/tabs/shortcuts/pull-request-keyboard-shortcuts-context';
import { getCurrentUser } from 'src/selectors/user-selectors';

type ApprovalButtonMessages = {
  approveLabel: FormattedMessage.MessageDescriptor;
  unapproveLabel: FormattedMessage.MessageDescriptor;
  approveAction: FormattedMessage.MessageDescriptor;
  unapproveAction: FormattedMessage.MessageDescriptor;
  approvedActionInfo: FormattedMessage.MessageDescriptor;
};

type ApprovalButtonProps = {
  messages: ApprovalButtonMessages;
  hasApproved: boolean;
  tabIndex?: number;
  approvedEventDate?: string;
  removeLeftBorderRadius?: boolean;
  isPrAuthor: boolean;
};

const ApprovalButton = ({
  hasApproved,
  messages,
  tabIndex,
  approvedEventDate,
  removeLeftBorderRadius,
  isPrAuthor,
}: ApprovalButtonProps) => {
  const approvalMessages = {
    tooltipMessage: messages.approvedActionInfo,
    makeActionLabel: messages.approveLabel,
    undoActionLabel: messages.unapproveLabel,
    makeActionMessage: hasApproved
      ? messages.unapproveAction
      : messages.approveAction,
  };

  const hasGreenCheckbox = !isPrAuthor || hasApproved;

  const dispatch = useDispatch();
  const sourceBranchHash = useSelector(getPullRequestSourceHash);
  const { owner, slug, id } = useSelector(getCurrentPullRequestUrlPieces);
  const currentUser = useSelector(getCurrentUser);

  const { approvePullRequestRef } = useContext(KeyboardShortcutsContext);

  const onRequestError = (e: Error) => {
    // Publish an error event so that the error message can be displayed.
    dispatch({ type: APPROVAL_ERROR, payload: { message: e.message } });
  };

  const onUnappprovalRequestSuccess = () => {
    // Publish a success event to trigger sagas to reload other parts of the PR.
    dispatch({
      type: APPROVAL_SUCCESS,
      payload: { currentUser, approval: false },
    });
    updateLastReviewedCommit(sourceBranchHash, owner, slug, id);
    // Publish a success event to trigger sagas to mark the iterative review as completed.
    dispatch({
      type: ITERATIVE_REVIEW_COMPLETED.TRUE,
    });
  };

  const onApprovalRequestSuccess = () => {
    // Publish a success event to trigger sagas to reload other parts of the PR.
    dispatch({
      type: APPROVAL_SUCCESS,
      payload: { currentUser, approval: true },
    });
    updateLastReviewedCommit(sourceBranchHash, owner, slug, id);
    // Publish a success event to trigger sagas to mark the iterative review as completed.
    dispatch({
      type: ITERATIVE_REVIEW_COMPLETED.TRUE,
    });
  };

  const {
    approvePullRequest,
    isLoading: approveIsLoading,
    isError: approveIsError,
  } = useApprovePullRequestWithMetrics({
    onError: onRequestError,
    onSuccess: onApprovalRequestSuccess,
  });
  const {
    unapprovePullRequest,
    isLoading: unapproveIsLoading,
    isError: unapproveIsError,
  } = useUnapprovePullRequest({
    onError: onRequestError,
    onSuccess: onUnappprovalRequestSuccess,
  });

  const onApprove = async () => {
    await approvePullRequest(owner, slug, id);
  };

  if (approvePullRequestRef) {
    approvePullRequestRef.current = () => {
      if (!hasApproved) {
        onApprove();
      }
    };
  }

  const onUnapprove = async () => {
    await unapprovePullRequest(owner, slug, id);
  };

  const ApproveButton = (
    <ReviewerActionButton
      messages={approvalMessages}
      mainIcon={
        <CheckCircleIcon
          primaryColor={hasGreenCheckbox ? colors.G300 : 'default'}
          label=""
        />
      }
      tabIndex={tabIndex}
      isError={approveIsError || unapproveIsError}
      isLoading={approveIsLoading || unapproveIsLoading}
      isDisabled={approveIsLoading || unapproveIsLoading}
      isSelected={hasApproved}
      actionDate={approvedEventDate ? new Date(approvedEventDate) : ''}
      makeAction={onApprove}
      undoAction={onUnapprove}
      removeLeftBorderRadius={removeLeftBorderRadius}
    />
  );

  // Show the keyboard shortcut tooltip only if in the IA view and the PR is not yet approved
  return !hasApproved ? (
    <KeyboardShortcutTooltip
      text={approvalMessages.makeActionMessage}
      shortcut="A"
      position="top"
    >
      {ApproveButton}
    </KeyboardShortcutTooltip>
  ) : (
    ApproveButton
  );
};

export default React.memo(ApprovalButton);
